// src/components/sidebar/DashboardSidebar.js
import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { useSidebar } from "../../context/SidebarContext";
import routeRegistry from "../../services/routeRegistry";
import UserSwitchModal from "../UserSwitchModal";
import BannerSlider from '../BannerSlider';
import { bannerService } from '../../services/bannerService';
import useFavoritePages from "../../hooks/useFavoritePages";
import useHeaderConfig from "../../hooks/useHeaderConfig";
import useSidebarConfig from "../../hooks/useSidebarConfig";
import logger from "../../utils/logger";
import cmsService from "../../services/cmsService";

import {
  FaShoppingCart, FaUserCircle,
  FaChevronDown, FaChevronRight,
  FaHome, FaBoxOpen, FaFileInvoiceDollar, FaUsers, FaTasks,
  FaShoppingBag, FaWarehouse, FaIndustry, FaTruck,
  FaMoneyBillWave, FaCalculator, FaCog, FaUserTie, FaBullseye, FaChartBar, FaQuestionCircle, FaBell,
  FaStar,
  FaTachometerAlt, FaMapMarkedAlt, FaRegListAlt, FaCartPlus, FaUserShield,
  FaRoute, FaMapMarkerAlt, FaBox, FaFileInvoice, FaChartLine, FaShieldAlt,
  FaBoxes, FaReceipt, FaFileImport, FaProjectDiagram, FaHammer,
  FaCalendarAlt, FaGlobe, FaHandshake, FaListUl, FaCalendarCheck,
  FaServer, FaTag, FaMobileAlt, FaClipboard, FaRobot, FaUniversity,
  FaBalanceScale, FaBook, FaSitemap, FaBuilding, FaUndo, FaExchangeAlt,
  FaPoundSign, FaClock, FaPercentage, FaExclamationTriangle,
  FaFileContract, FaImage, FaFilePdf, FaLayerGroup, FaEnvelope,
  FaUsersCog, FaCogs, FaClipboardList, FaUserTag, FaEyeSlash, FaSearch,
  FaChartPie, FaChartArea, FaPoll, FaPollH, FaSignal,
  FaCoins, FaWallet, FaDollarSign, FaCreditCard, FaPiggyBank, FaCashRegister,
  FaMoneyCheck, FaFunnelDollar, FaSearchDollar,
  FaStore, FaStoreAlt, FaCubes, FaPallet, FaDolly, FaShippingFast,
  FaTruckLoading, FaShip, FaPlane, FaAnchor, FaBarcode,
  FaWrench, FaTools, FaBriefcase, FaArchive, FaDatabase,
  FaFolder, FaFolderOpen, FaNewspaper, FaPencilAlt, FaFilter,
  FaFlag, FaAward, FaTrophy, FaMedal, FaCrown, FaGem,
  FaBolt, FaFire, FaMagic, FaLightbulb, FaPuzzlePiece, FaCrosshairs,
  FaKey, FaLock, FaEye, FaCheckCircle, FaStopwatch, FaHourglass,
  FaSyncAlt, FaRandom, FaCompass, FaWeight, FaSpider, FaDownload
} from 'react-icons/fa';
import { FiStar } from 'react-icons/fi';

import { routeIcons, routeLabels } from '../../constants/routes';
import { useTranslation } from '../../i18n';

const CMS_EDITOR_ROUTES = ['/cms/pages', '/cms/posts', '/cms/menus', '/cms/chrome'];

export const ROUTE_CATEGORY_MAP = {
  '/products': 'Online Shop',
  '/cart': 'Online Shop',
  '/checkout': 'Online Shop',
  '/carts-management': 'Online Shop',
  '/dashboard': 'Main',
  '/profile': 'Main',
  '/products/admin': 'Products',
  '/barcodes': 'Products',
  '/prices': 'Products',
  '/discounts': 'Products',
  '/banners': 'Products',
  '/catalogue-generator': 'Products',
  '/orders': 'Sales',
  '/invoices': 'Sales',
  '/credit-notes': 'Sales',
  '/customers': 'Sales',
  '/sales-assignments': 'Sales',
  '/customer-assignments': 'Sales',
  '/customer-manager-assignments': 'Sales',
  '/payment-terms': 'Sales',
  '/customer-segmentation-settings': 'Sales',
  '/warehouses': 'Warehousing',
  '/receipt': 'Warehousing',
  '/picking': 'Warehousing',
  '/packing': 'Warehousing',
  '/warehousing/inventory': 'Warehousing',
  '/warehousing/stocktake': 'Warehousing',
  '/warehousing/transfers': 'Warehousing',
  '/production/dashboard': 'Production',
  '/production/planner': 'Production',
  '/production/weekly-plan': 'Production',
  '/production/skills': 'Production',
  '/production/bom': 'Production',
  '/production/orders': 'Production',
  '/production/workcentres': 'Production',
  '/production/planning': 'Production',
  '/production/wip': 'Production',
  '/production/wip-v2': 'Production',
  '/production/station': 'Production',
  '/production/workstation': 'Production',
  '/production/releases': 'Production',
  '/production/releases/:id': 'Production',
  '/production/qr-scanner': 'Production',
  '/production/reports': 'Production',
  '/production/operations': 'Production',
  '/purchase-order': 'Purchases',
  '/suppliers': 'Purchases',
  '/purchase-planner': 'Purchases',
  '/purchase-invoices': 'Purchases',
  '/debit-notes': 'Purchases',
  '/website-scraper': 'Purchases',
  '/scraped-products': 'Purchases',
  '/logistics': 'Logistics',
  '/logistics/routes': 'Logistics',
  '/logistics/drivers': 'Logistics',
  '/logistics/tracking': 'Logistics',
  '/logistics/deliveries': 'Logistics',
  '/logistics/vehicles': 'Logistics',
  '/logistics/route-planner': 'Logistics',
  '/logistics/locations': 'Logistics',
  '/logistics/delivery-zones': 'Logistics',
  '/logistics/zone-neighbors': 'Logistics',
  '/fulfillment': 'Logistics',
  '/map': 'Logistics',
  '/crm': 'Organiser',
  '/crm/dashboard': 'Organiser',
  '/crm/leads': 'Organiser',
  '/crm/events': 'Organiser',
  '/crm/calls': 'Organiser',
  '/organizer/emails': 'Organiser',
  '/organizer': 'Organiser',
  '/organizer/dashboard': 'Organiser',
  '/organizer/tasks': 'Organiser',
  '/organizer/projects': 'Organiser',
  '/organizer/documents': 'Organiser',
  '/cms/pages': 'CMS',
  '/cms/posts': 'CMS',
  '/cms/menus': 'CMS',
  '/cms/chrome': 'CMS',
  '/cms/settings': 'CMS',
  '/users': 'Admin',
  '/settings': 'Admin',
  '/system-logs': 'Admin',
  '/system/memory-monitoring': 'Admin',
  '/permissions-dashboard': 'Admin',
  '/permission-control': 'Admin',
  '/header-config': 'Admin',
  '/sidebar-config': 'Admin',
  '/customer-permissions': 'Admin',
  '/assistant': 'Main',
  '/assistant/settings': 'Main',
  '/cashmanagement/accounts': 'Cash Management',
  '/cashmanagement/transactions': 'Cash Management',
  '/cashmanagement/customer-statement': 'Cash Management',
  '/cashmanagement/supplier-statement': 'Cash Management',
  '/cashmanagement/transfers': 'Cash Management',
  '/cashmanagement/loans': 'Cash Management',
  '/cashmanagement/scheduled-payments': 'Cash Management',
  '/cashmanagement/opening-balances': 'Cash Management',
  '/cashmanagement/reconciliation': 'Cash Management',
  '/cashmanagement/reports': 'Cash Management',
  '/cashmanagement/settings': 'Cash Management',
  '/accounting/chart-of-accounts': 'Accounting',
  '/accounting/account-balances': 'Accounting',
  '/accounting/journal-entries': 'Accounting',
  '/accounting/fiscal-periods': 'Accounting',
  '/accounting/reports': 'Accounting',
  '/accounting/general-ledger': 'Accounting',
  '/accounting/fixed-assets': 'Accounting',
  '/accounting/settings': 'Accounting',
  '/payroll': 'Payroll',
  '/payroll/dashboard': 'Payroll',
  '/payroll/employees': 'Payroll',
  '/payroll/pay-periods': 'Payroll',
  '/payroll/entries': 'Payroll',
  '/payroll/scenarios': 'Payroll',
  '/payroll/timesheets': 'Payroll',
  '/payroll/schedules': 'Payroll',
  '/sales-targets': 'Sales Targets',
  '/sales-targets/remuneration-rules': 'Sales Targets',
  '/sales-targets/penalization-rules': 'Sales Targets',
  '/sales-targets/calculations': 'Sales Targets',
  '/mobile/logistics': 'Logistics',
  '/mobile/routes': 'Logistics',
  '/mobile/deliveries': 'Logistics',
  '/mobile/tracking': 'Logistics',
  '/mobile/inspection': 'Logistics',
  '/mobile/orders': 'Sales',
  '/mobile/settings': 'Admin',
  '/analytics': 'Analytics',
  '/analytics/sales': 'Analytics',
  '/analytics/purchases': 'Analytics',
  '/analytics/production': 'Analytics',
  '/analytics/warehousing': 'Analytics',
  '/analytics/logistics': 'Analytics',
  '/analytics/cash-management': 'Analytics',
  '/analytics/payroll': 'Analytics',
  '/alerts': 'Alerts',
  '/help-centre': 'Help'
};

// Category icons for collapsed group display
const categoryIcons = {
  'Main': FaHome,
  'Products': FaBoxOpen,
  'Sales': FaFileInvoiceDollar,
  'CRM': FaUsers,
  'Office': FaTasks,
  'Organiser': FaTasks,
  'CMS': FaGlobe,
  'Purchases': FaShoppingBag,
  'Warehousing': FaWarehouse,
  'Production': FaIndustry,
  'Logistics': FaTruck,
  'Cash Management': FaMoneyBillWave,
  'Payroll': FaUserTie,
  'Sales Targets': FaBullseye,
  'Analytics': FaChartBar,
  'Accounting': FaCalculator,
  'Admin': FaCog,
  'Alerts': FaBell,
  'Help': FaQuestionCircle,
  'Online Shop': FaStore,
  'Other': FaShoppingCart
};

const ICON_MAP = {
  'FaHome': FaHome, 'FaTachometerAlt': FaTachometerAlt,
  'FaBoxOpen': FaBoxOpen, 'FaBox': FaBox, 'FaBoxes': FaBoxes,
  'FaShoppingCart': FaShoppingCart, 'FaCartPlus': FaCartPlus, 'FaShoppingBag': FaShoppingBag,
  'FaFileInvoiceDollar': FaFileInvoiceDollar, 'FaFileInvoice': FaFileInvoice,
  'FaFileImport': FaFileImport, 'FaFileContract': FaFileContract, 'FaFilePdf': FaFilePdf,
  'FaReceipt': FaReceipt,
  'FaUsers': FaUsers, 'FaUsersCog': FaUsersCog, 'FaUserTie': FaUserTie,
  'FaUserTag': FaUserTag, 'FaUserShield': FaUserShield, 'FaHandshake': FaHandshake,
  'FaTasks': FaTasks, 'FaClipboardList': FaClipboardList, 'FaClipboard': FaClipboard,
  'FaRegListAlt': FaRegListAlt, 'FaListUl': FaListUl,
  'FaWarehouse': FaWarehouse, 'FaIndustry': FaIndustry,
  'FaHammer': FaHammer, 'FaProjectDiagram': FaProjectDiagram,
  'FaTruck': FaTruck, 'FaRoute': FaRoute,
  'FaMapMarkerAlt': FaMapMarkerAlt, 'FaMapMarkedAlt': FaMapMarkedAlt, 'FaGlobe': FaGlobe,
  'FaMoneyBillWave': FaMoneyBillWave, 'FaUniversity': FaUniversity,
  'FaBalanceScale': FaBalanceScale, 'FaPoundSign': FaPoundSign, 'FaExchangeAlt': FaExchangeAlt,
  'FaCalculator': FaCalculator, 'FaBook': FaBook, 'FaSitemap': FaSitemap, 'FaBuilding': FaBuilding,
  'FaChartBar': FaChartBar, 'FaChartLine': FaChartLine,
  'FaBullseye': FaBullseye, 'FaPercentage': FaPercentage,
  'FaCalendarAlt': FaCalendarAlt, 'FaCalendarCheck': FaCalendarCheck, 'FaClock': FaClock,
  'FaCog': FaCog, 'FaCogs': FaCogs, 'FaShieldAlt': FaShieldAlt, 'FaServer': FaServer,
  'FaLayerGroup': FaLayerGroup, 'FaTag': FaTag, 'FaImage': FaImage,
  'FaRobot': FaRobot, 'FaEnvelope': FaEnvelope, 'FaMobileAlt': FaMobileAlt,
  'FaUndo': FaUndo, 'FaBell': FaBell, 'FaQuestionCircle': FaQuestionCircle,
  'FaExclamationTriangle': FaExclamationTriangle, 'FaEyeSlash': FaEyeSlash, 'FaSearch': FaSearch,
  'FaChartPie': FaChartPie, 'FaChartArea': FaChartArea,
  'FaPoll': FaPoll, 'FaPollH': FaPollH, 'FaSignal': FaSignal, 'FaCrosshairs': FaCrosshairs,
  'FaCoins': FaCoins, 'FaWallet': FaWallet, 'FaDollarSign': FaDollarSign,
  'FaCreditCard': FaCreditCard, 'FaPiggyBank': FaPiggyBank, 'FaCashRegister': FaCashRegister,
  'FaMoneyCheck': FaMoneyCheck, 'FaFunnelDollar': FaFunnelDollar, 'FaSearchDollar': FaSearchDollar,
  'FaStore': FaStore, 'FaStoreAlt': FaStoreAlt, 'FaCubes': FaCubes,
  'FaPallet': FaPallet, 'FaDolly': FaDolly, 'FaShippingFast': FaShippingFast,
  'FaTruckLoading': FaTruckLoading, 'FaShip': FaShip, 'FaPlane': FaPlane,
  'FaAnchor': FaAnchor, 'FaBarcode': FaBarcode, 'FaSpider': FaSpider, 'FaDownload': FaDownload,
  'FaWrench': FaWrench, 'FaTools': FaTools, 'FaBriefcase': FaBriefcase,
  'FaArchive': FaArchive, 'FaDatabase': FaDatabase,
  'FaFolder': FaFolder, 'FaFolderOpen': FaFolderOpen, 'FaNewspaper': FaNewspaper,
  'FaPencilAlt': FaPencilAlt, 'FaFilter': FaFilter,
  'FaFlag': FaFlag, 'FaAward': FaAward, 'FaTrophy': FaTrophy,
  'FaMedal': FaMedal, 'FaCrown': FaCrown, 'FaGem': FaGem,
  'FaBolt': FaBolt, 'FaFire': FaFire, 'FaMagic': FaMagic,
  'FaLightbulb': FaLightbulb, 'FaPuzzlePiece': FaPuzzlePiece,
  'FaKey': FaKey, 'FaLock': FaLock, 'FaEye': FaEye,
  'FaCheckCircle': FaCheckCircle, 'FaStopwatch': FaStopwatch, 'FaHourglass': FaHourglass,
  'FaSyncAlt': FaSyncAlt, 'FaRandom': FaRandom, 'FaCompass': FaCompass, 'FaWeight': FaWeight,
};

const DashboardSidebar = ({ onToggleSettings, settingsOpen, headerHeight = 0 }) => {
  const { t } = useTranslation();
  const { user, routesLoaded, userRoles, isSuperuser, access, hasPagePermission } = useContext(AuthContext);
  const {
    leftCollapsed,
    leftWidth,
    isLeftResizing,
    toggleLeftSidebar,
    handleLeftMouseDown,
    isMobile,
    leftHidden
  } = useSidebar();
  const location = useLocation();
  const { isFavorite, toggleFavorite } = useFavoritePages();
  const { hasHeader } = useHeaderConfig();
  const { configuration: sidebarConfiguration } = useSidebarConfig();
  const isTwoColumn = sidebarConfiguration?.sidebar_type === 'two-column';
  const [showUserSwitchModal, setShowUserSwitchModal] = useState(false);
  const [clickedPath, setClickedPath] = useState(null);
  const [banners, setBanners] = useState([]);
  const [bannerLocationSettings, setBannerLocationSettings] = useState(null);

  // Collapsed categories state with localStorage persistence (for expanded sidebar)
  const [collapsedCategories, setCollapsedCategories] = useState(() => {
    try {
      const saved = localStorage.getItem('sidebarCollapsedCategories');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Expanded category for collapsed sidebar (accordion - only one open at a time)
  const [expandedCollapsedCategory, setExpandedCollapsedCategory] = useState(null);

  // Two-column sidebar mode: whether the rail is hover-expanded (showing category names),
  // and which category's routes are currently displayed.
  const [railExpanded, setRailExpanded] = useState(false);
  const [twoColumnCategory, setTwoColumnCategory] = useState(null);

  const mobileBaseTop = hasHeader ? 56 : 0;
  const mobileFullTop = hasHeader ? (headerHeight || 56) : 0;
  const [mobileTopOffset, setMobileTopOffset] = useState(mobileFullTop);

  React.useEffect(() => {
    if (!isMobile) return;
    if (!hasHeader) {
      setMobileTopOffset(0);
      return;
    }

    setMobileTopOffset(mobileFullTop);

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const newTop = Math.max(mobileBaseTop, mobileFullTop - scrollY);
      setMobileTopOffset(newTop);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile, hasHeader, mobileBaseTop, mobileFullTop]);

  // Fetch banners for left_sidebar location
  useEffect(() => {
    const fetchBanners = async () => {
      if (!user) return;
      try {
        const result = await bannerService.getActiveBanners('left_sidebar');
        if (result.success) {
          const bannersData = result.banners || result.data || [];
          setBanners(bannersData);
          if (result.locationSettings) {
            setBannerLocationSettings(result.locationSettings);
          }
        }
      } catch (err) {
        console.error('[DashboardSidebar] Error fetching banners:', err);
      }
    };
    fetchBanners();
  }, [user]);

  // Reset clicked path when location changes
  React.useEffect(() => {
    setClickedPath(null);
    setRailExpanded(false);
  }, [location.pathname]);

  // Close expanded category when sidebar expands
  React.useEffect(() => {
    if (!leftCollapsed) {
      setExpandedCollapsedCategory(null);
    }
  }, [leftCollapsed]);

  // Check if user is logged in
  const shouldShowSidebar = () => {
    if (!user) return false;
    return true;
  };

  // Get accessible routes directly from the route registry (server-provided)
  const [accessibleLinks, setAccessibleLinks] = React.useState([]);
  const [cmsStaticMode, setCmsStaticMode] = React.useState(false);

  useEffect(() => {
    cmsService.getSiteSettings()
      .then(data => setCmsStaticMode((data.content || {}).site_mode === 'static'))
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!routesLoaded) {
      // Always show products page even when routes aren't loaded
      const InitialIcon = routeIcons["/"] || FaShoppingCart;
      setAccessibleLinks([{
        path: "/products",
        icon: <InitialIcon />,
        text: routeLabels["/"]
      }]);
      return;
    }
    
    try {
      const accessibleRoutes = routeRegistry.getAllRoutes().filter(route => hasPagePermission(route.path));
      
      const links = accessibleRoutes
        .filter(route => {
          // Only show active routes
          if (!route.active) return false;

          // Filter out detail routes and API routes that shouldn't be in sidebar
          const path = route.path;

          // Skip routes with dynamic parameters (containing angle brackets or ending with slash after ID)
          if (path.includes('<') || path.includes('>')) return false;

          // Skip API routes (most navigation routes start with / not /api/)
          if (path.startsWith('/api/')) return false;

          // Skip detail routes (containing /detail or ending with ID patterns or :id)
          if (path.includes('/detail') || /\/\d+\/?$/.test(path) || path.includes('/:id')) return false;

          // Skip mobile-specific routes
          if (path.includes('/mobile/')) return false;

          // Only include routes that have corresponding frontend pages
          const hasIcon = routeIcons[path] !== undefined;
          return hasIcon;
        })
        .map(route => {
          const IconComponent = routeIcons[route.path] || FaShoppingCart;
          // Map "/" to "/products" for proper routing (App.js redirects / to /products)
          const actualPath = route.path === '/' ? '/products' : route.path;
          return {
            path: actualPath,
            icon: <IconComponent />,
            text: routeLabels[route.path] || route.title || route.name || route.path
          };
        });

      // Remove duplicates - DON'T hardcode products page, respect permissions
      const uniqueLinks = [];
      links.forEach(link => {
        if (!uniqueLinks.find(l => l.path === link.path)) {
          uniqueLinks.push(link);
        }
      });

      const visibleLinks = cmsStaticMode
        ? uniqueLinks.filter(link => !CMS_EDITOR_ROUTES.includes(link.path))
        : uniqueLinks;

      logger.log('[DashboardSidebar] Final unique links:', visibleLinks);
      setAccessibleLinks(visibleLinks);
    } catch (error) {
      console.error('[DashboardSidebar] Error getting routes:', error);
      // Fallback to just products page
      const FallbackIcon = routeIcons["/"] || FaShoppingCart;
      setAccessibleLinks([{
        path: "/products",
        icon: <FallbackIcon />,
        text: routeLabels["/"]
      }]);
    }
  }, [routesLoaded, cmsStaticMode, access]); // eslint-disable-line react-hooks/exhaustive-deps

  // Group links by category
  const groupedLinks = React.useMemo(() => {
    if (sidebarConfiguration && sidebarConfiguration.categories) {
      const hiddenSet = new Set(sidebarConfiguration.hidden_routes || []);
      const iconOverrides = sidebarConfiguration.route_icons || {};
      const labelOverrides = sidebarConfiguration.route_labels || {};
      const assignedRoutes = new Set();

      const applyOverrides = (link) => {
        const hasIconOverride = iconOverrides[link.path];
        const hasLabelOverride = labelOverrides[link.path];
        if (!hasIconOverride && !hasLabelOverride) return link;
        const overridden = { ...link };
        if (hasIconOverride && ICON_MAP[hasIconOverride]) {
          const OverrideIcon = ICON_MAP[hasIconOverride];
          overridden.icon = <OverrideIcon />;
        }
        if (hasLabelOverride) overridden.text = hasLabelOverride;
        return overridden;
      };

      const groups = sidebarConfiguration.categories
        .map(cat => {
          const catRoutes = (cat.routes || []).filter(routePath => {
            assignedRoutes.add(routePath);
            return !hiddenSet.has(routePath);
          });
          const matchedLinks = catRoutes
            .map(routePath => accessibleLinks.find(link => link.path === routePath))
            .filter(Boolean)
            .map(applyOverrides);
          return [cat.name, matchedLinks, cat.color || 'bg-gray-50', cat.icon];
        })
        .filter(([, links]) => links.length > 0);

      const unassigned = accessibleLinks.filter(
        link => !assignedRoutes.has(link.path) && !hiddenSet.has(link.path)
      );
      if (unassigned.length > 0) {
        const groupIndex = {};
        groups.forEach((g, i) => { groupIndex[g[0]] = i; });
        const leftovers = [];
        unassigned.forEach(link => {
          const mappedCategory = ROUTE_CATEGORY_MAP[link.path];
          if (mappedCategory) {
            if (groupIndex[mappedCategory] !== undefined) {
              groups[groupIndex[mappedCategory]][1].push(link);
            } else {
              groupIndex[mappedCategory] = groups.length;
              groups.push([mappedCategory, [link], 'bg-gray-50', undefined]);
            }
          } else {
            leftovers.push(link);
          }
        });
        if (leftovers.length > 0) {
          groups.push(['Other', leftovers, 'bg-gray-50', 'FaShoppingCart']);
        }
      }

      return groups;
    }


    const categoryColors = {
      'Main': 'bg-blue-50',
      'Products': 'bg-green-50',
      'Sales': 'bg-orange-50',
      'CRM': 'bg-pink-50',
      'Office': 'bg-yellow-50',
      'Organiser': 'bg-yellow-50',
      'CMS': 'bg-indigo-50',
      'Purchases': 'bg-teal-50',
      'Warehousing': 'bg-cyan-50',
      'Production': 'bg-indigo-50',
      'Logistics': 'bg-purple-50',
      'Cash Management': 'bg-emerald-50',
      'Payroll': 'bg-violet-50',
      'Sales Targets': 'bg-rose-50',
      'Analytics': 'bg-sky-50',
      'Accounting': 'bg-amber-50',
      'Admin': 'bg-red-50',
      'Alerts': 'bg-red-50',
      'Help': 'bg-blue-50',
      'Online Shop': 'bg-lime-50',
      'Other': 'bg-gray-50'
    };

    const groups = accessibleLinks.reduce((acc, link) => {
      const category = ROUTE_CATEGORY_MAP[link.path] || 'Other';
      if (!acc[category]) acc[category] = [];
      acc[category].push(link);
      return acc;
    }, {});

    // Define order of categories
    const categoryOrder = ['Main', 'Online Shop', 'Products', 'Sales', 'CRM', 'Organiser', 'CMS', 'Office', 'Purchases', 'Warehousing', 'Production', 'Logistics', 'Cash Management', 'Payroll', 'Sales Targets', 'Analytics', 'Accounting', 'Admin', 'Alerts', 'Help', 'Other'];
    
    return categoryOrder
      .filter(cat => groups[cat] && groups[cat].length > 0)
      .map(cat => [cat, groups[cat], categoryColors[cat]]);
  }, [accessibleLinks, sidebarConfiguration]);

  // Get the category that contains the active link
  const getActiveLinkCategory = React.useCallback(() => {
    for (const [category, links] of groupedLinks) {
      const hasActive = links.some(link => {
        const isExactMatch = location.pathname === link.path;
        const isParentMatch = link.path !== "/" && link.path !== "/products" &&
          location.pathname.startsWith(link.path + '/');
        const isProductsMatch = link.path === "/products" &&
          (location.pathname === "/" || location.pathname === "/products");
        return isExactMatch || isParentMatch || isProductsMatch;
      });
      if (hasActive) return category;
    }
    return null;
  }, [groupedLinks, location.pathname]);

  // Toggle category - active category click toggles all categories
  const toggleCategory = (category) => {
    const activeCategory = getActiveLinkCategory();

    if (leftCollapsed) {
      // In collapsed sidebar mode
      if (category === activeCategory) {
        // Active category click: toggle all inactive categories
        // Check if any inactive category is expanded
        const hasExpandedInactive = expandedCollapsedCategory !== null;
        if (hasExpandedInactive) {
          // Collapse all (active stays expanded by design)
          setExpandedCollapsedCategory(null);
        }
        // If none expanded, do nothing (active is always shown)
      } else {
        // For inactive categories: toggle between this category and none
        setExpandedCollapsedCategory(prev => prev === category ? null : category);
      }
    } else {
      // Expanded sidebar mode
      if (category === activeCategory) {
        // Active category click: toggle all categories (collapse all / expand all)
        const allCategories = groupedLinks.map(([cat]) => cat);
        const inactiveCategories = allCategories.filter(cat => cat !== activeCategory);

        // Check if all inactive categories are collapsed
        const allInactiveCollapsed = inactiveCategories.every(cat => collapsedCategories[cat]);

        setCollapsedCategories(prev => {
          const updated = { ...prev };
          // Toggle all inactive categories
          inactiveCategories.forEach(cat => {
            updated[cat] = !allInactiveCollapsed; // Expand all if all collapsed, collapse all if any expanded
          });
          // Active category always stays expanded
          updated[activeCategory] = false;
          localStorage.setItem('sidebarCollapsedCategories', JSON.stringify(updated));
          return updated;
        });
      } else {
        // Regular category toggle
        setCollapsedCategories(prev => {
          const updated = { ...prev, [category]: !prev[category] };
          localStorage.setItem('sidebarCollapsedCategories', JSON.stringify(updated));
          return updated;
        });
      }
    }
  };

  // Auto-expand active category and ensure it stays expanded
  React.useEffect(() => {
    const activeCategory = getActiveLinkCategory();
    if (activeCategory) {
      // For expanded sidebar: ensure active category is not collapsed
      if (!leftCollapsed && collapsedCategories[activeCategory]) {
        setCollapsedCategories(prev => {
          const updated = { ...prev, [activeCategory]: false };
          localStorage.setItem('sidebarCollapsedCategories', JSON.stringify(updated));
          return updated;
        });
      }
      // For collapsed sidebar: auto-expand active category
      if (leftCollapsed && expandedCollapsedCategory !== activeCategory) {
        setExpandedCollapsedCategory(activeCategory);
      }
    }
  }, [location.pathname, getActiveLinkCategory, leftCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Two-column mode: select the category containing the active route
  React.useEffect(() => {
    const activeCategory = getActiveLinkCategory();
    if (activeCategory) {
      setTwoColumnCategory(activeCategory);
    }
  }, [location.pathname, getActiveLinkCategory]);

  // Determine Role Title
  let roleTitle = t("Dashboard");
  if (isSuperuser) roleTitle = t("Superuser");
  else if (userRoles.includes('admin')) roleTitle = t("Admin Dashboard");
  else if (userRoles.includes('salesManager')) roleTitle = t("Sales Manager");
  else if (userRoles.includes('salesRep')) roleTitle = t("Sales Rep");
  else if (userRoles.includes('customerManager')) roleTitle = t("Customer Manager");
  else if (userRoles.includes('customer')) roleTitle = t("Customer Dashboard");

  // Don't render if user is not logged in or sidebar is hidden
  if (!shouldShowSidebar() || leftHidden) return null;

  // Render overlay when sidebar is open on mobile
  const renderOverlay = () => {
    if (!isMobile || leftCollapsed) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50"
        style={{ zIndex: 44 }}
        onClick={() => toggleLeftSidebar(true)}
        aria-label={t("Close Menu")}
      />
    );
  };

  return (
    <>
      {/* Overlay for mobile */}
      {renderOverlay()}
      
      {/* Sidebar - Fixed position, under header (and customer selector on mobile) */}
      <aside
        className={`fixed left-0 bg-white shadow-md transition-all duration-300 ease-in-out ${
          isTwoColumn ? 'overflow-hidden flex flex-col' : 'overflow-y-auto overflow-x-hidden scrollbar-hide'
        } ${isMobile ? 'mobile-sidebar-left' : ''}`}
        style={{
          width: leftCollapsed && !isMobile ? '80px' : `${leftWidth}px`,
          transform: isMobile && leftCollapsed ? 'translateX(-100%)' : 'translateX(0)',
          transition: isLeftResizing ? 'none' : 'width 0.3s ease-in-out, transform 0.3s ease-in-out',
          top: isMobile ? `${mobileTopOffset}px` : `${headerHeight}px`,
          bottom: 0,
          zIndex: 45 // Higher than content (40) but lower than header (50)
        }}
      >
        {/* Header Section - Sticky on mobile, extra top padding on desktop */}
        <div className={`px-3 pb-2 bg-white flex-shrink-0 ${isMobile ? 'pt-2 sticky top-0 z-10 border-b border-gray-100' : 'pt-6 mb-1'}`}>
          {!leftCollapsed ? (
            <button
              onClick={() => setShowUserSwitchModal(true)}
              className="flex items-center gap-2 w-full hover:bg-gray-50 rounded-lg p-1 transition-all"
              title="Click to switch user"
            >
              <FaUserCircle className="text-primary text-xl flex-shrink-0" />
              <span className="text-sm truncate">
                <span className="text-primary font-semibold">{user?.name || user?.username}</span>
                <span className="text-gray-400">: {roleTitle.toLowerCase()}</span>
              </span>
            </button>
          ) : (
            <button
              onClick={() => setShowUserSwitchModal(true)}
              className="hover:opacity-80 transition-opacity cursor-pointer w-full flex justify-center"
              title={`${user?.name || user?.username}: ${roleTitle}`}
            >
              <FaUserCircle className="text-primary text-2xl" />
            </button>
          )}
        </div>

        {/* Navigation Links */}
        <nav className={`px-3 pb-4 pt-1 ${isTwoColumn ? 'flex-1 min-h-0' : ''}`}>
          {isTwoColumn ? (() => {
            const isLinkActive = (link) => {
              const isExactMatch = location.pathname === link.path;
              const isParentMatch = link.path !== "/" && link.path !== "/products" &&
                location.pathname.startsWith(link.path + '/');
              const isProductsMatch = link.path === "/products" &&
                (location.pathname === "/" || location.pathname === "/products");
              return isExactMatch || isParentMatch || isProductsMatch;
            };
            const selectedName = groupedLinks.some(([cat]) => cat === twoColumnCategory)
              ? twoColumnCategory
              : (groupedLinks[0] ? groupedLinks[0][0] : null);
            const selectedGroup = groupedLinks.find(([cat]) => cat === selectedName);
            const showRoutes = !leftCollapsed && selectedGroup;

            return (
              <div className="flex gap-2 h-full relative">
                {/* Category rail — expands in place on hover to reveal names */}
                <div
                  className={`flex flex-col gap-1.5 flex-shrink-0 overflow-y-auto overflow-x-hidden scrollbar-hide p-1 transition-all duration-200 ${
                    leftCollapsed ? 'w-full items-center' : 'w-14 items-center'
                  } ${
                    railExpanded && !leftCollapsed ? 'absolute left-0 top-0 bottom-0 z-10 !w-52 !items-stretch bg-white shadow-xl rounded-lg p-2' : ''
                  }`}
                  onMouseEnter={() => { if (!isMobile && !leftCollapsed) setRailExpanded(true); }}
                  onMouseLeave={() => { if (!isMobile) setRailExpanded(false); }}
                >
                  {groupedLinks.map(([category, links, bgColor, iconKey]) => {
                    const CategoryIcon = (iconKey ? ICON_MAP[iconKey] : null) || categoryIcons[category] || FaShoppingCart;
                    const hasActiveLink = links.some(isLinkActive);
                    const isSelected = category === selectedName;
                    const showLabel = railExpanded && !leftCollapsed;
                    return (
                      <button
                        key={category}
                        onClick={() => {
                          setTwoColumnCategory(category);
                          setRailExpanded(false);
                          if (leftCollapsed && !isMobile) {
                            toggleLeftSidebar();
                          }
                        }}
                        className={`h-12 rounded-xl flex items-center flex-shrink-0 transition-all ${bgColor} ${
                          showLabel ? 'w-full px-3 gap-3 justify-start' : 'w-12 justify-center'
                        } ${
                          isSelected && showRoutes
                            ? 'ring-2 ring-primary ring-opacity-40 shadow-sm'
                            : 'hover:ring-1 hover:ring-gray-300'
                        }`}
                        title={showLabel ? '' : t(category)}
                      >
                        <CategoryIcon className={`text-lg flex-shrink-0 ${hasActiveLink ? 'text-primary' : 'text-gray-600'}`} />
                        {showLabel && (
                          <span className={`text-sm font-medium truncate ${hasActiveLink ? 'text-primary' : 'text-gray-700'}`}>
                            {t(category)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Routes column for the selected category */}
                {showRoutes && (() => {
                  const [, links, bgColor] = selectedGroup;
                  const categoryHasActive = links.some(isLinkActive);
                  return (
                    <div className={`flex-1 min-w-0 rounded-lg self-start max-h-full overflow-y-auto scrollbar-hide ${
                      categoryHasActive
                        ? 'bg-primary bg-opacity-10 ring-2 ring-primary ring-opacity-30'
                        : bgColor
                    }`}>
                      <h6 className={`px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider ${
                        categoryHasActive ? 'text-primary' : 'text-gray-600'
                      }`}>
                        {selectedName}
                      </h6>
                      <ul className="space-y-1 px-2 pb-2">
                        {links.map((link) => {
                          const isExactMatch = location.pathname === link.path;
                          const isParentMatch = !isExactMatch &&
                            link.path !== "/" &&
                            link.path !== "/products" &&
                            location.pathname.startsWith(link.path + '/') &&
                            !links.some(l => l.path !== link.path && location.pathname === l.path);
                          const isProductsMatch = link.path === "/products" &&
                            (location.pathname === "/" || location.pathname === "/products");

                          const isActive = isExactMatch || isParentMatch || isProductsMatch;
                          const isClicked = clickedPath === link.path;
                          const shouldHighlight = isActive || isClicked;

                          const isFav = isFavorite(link.path);
                          return (
                            <li key={link.path} className="group">
                              <div className="flex items-center">
                                <Link
                                  to={link.to || link.path}
                                  className={`flex-1 flex items-center py-2 px-3 rounded-md transition-colors ${
                                    shouldHighlight
                                      ? 'bg-primary bg-opacity-10 text-primary'
                                      : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                                  onClick={() => {
                                    setClickedPath(link.path);
                                    if (isMobile) {
                                      toggleLeftSidebar(true);
                                    }
                                  }}
                                >
                                  <span className="mr-3">{link.icon}</span>
                                  <span>{t(link.text)}</span>
                                </Link>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFavorite(link.path, link.text);
                                  }}
                                  className={`p-1 rounded transition-all flex-shrink-0 ${
                                    isFav
                                      ? 'text-yellow-400 opacity-100'
                                      : 'text-gray-300 opacity-0 group-hover:opacity-100 hover:text-yellow-400'
                                  }`}
                                  title={isFav ? t('Remove from favorites') : t('Add to favorites')}
                                >
                                  {isFav ? <FaStar className="text-sm" /> : <FiStar className="text-sm" />}
                                </button>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })()}
              </div>
            );
          })() : groupedLinks.map(([category, links, bgColor, iconKey]) => {
            const isGroupCollapsed = collapsedCategories[category];
            const CategoryIcon = (iconKey ? ICON_MAP[iconKey] : null) || categoryIcons[category] || FaShoppingCart;

            // Check if any link in this category is active
            const hasActiveLink = links.some(link => {
              const isExactMatch = location.pathname === link.path;
              const isParentMatch = link.path !== "/" && link.path !== "/products" &&
                location.pathname.startsWith(link.path + '/');
              const isProductsMatch = link.path === "/products" &&
                (location.pathname === "/" || location.pathname === "/products");
              return isExactMatch || isParentMatch || isProductsMatch;
            });

            return (
              <div key={category} className={`mb-2 rounded-lg overflow-hidden ${
                hasActiveLink
                  ? 'bg-primary bg-opacity-10 ring-2 ring-primary ring-opacity-30'
                  : bgColor
              }`}>
                {/* Category Header - Always clickable */}
                {(() => {
                  const isCategoryExpanded = leftCollapsed && (hasActiveLink || expandedCollapsedCategory === category);

                  return (
                    <button
                      onClick={() => toggleCategory(category)}
                      className={`w-full flex items-center p-2 hover:bg-black hover:bg-opacity-5 transition-colors ${
                        leftCollapsed ? 'justify-center' : 'justify-between'
                      }`}
                      title={leftCollapsed ? category : ''}
                    >
                      {leftCollapsed ? (
                        // Collapsed sidebar: show category icon (smaller when expanded)
                        isCategoryExpanded ? (
                          // When expanded: show small label at top
                          <div className="flex flex-col items-center w-full">
                            <div className={`text-[10px] font-bold uppercase tracking-wider px-1 py-0.5 rounded ${
                              hasActiveLink ? 'text-primary' : 'text-gray-500'
                            }`}>
                              {category.split(' ')[0].substring(0, 4)}
                            </div>
                          </div>
                        ) : (
                          // When collapsed: show normal icon
                          <div className={`p-2 rounded-lg transition-all ${
                            hasActiveLink
                              ? 'bg-primary text-white shadow-md'
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}>
                            <CategoryIcon className="text-lg" />
                          </div>
                        )
                      ) : (
                        // Expanded sidebar: show category name and chevron
                        <>
                          <div className="flex items-center gap-2">
                            {isGroupCollapsed && (
                              <div className={`p-1 rounded ${hasActiveLink ? 'bg-primary text-white' : ''}`}>
                                <CategoryIcon className={`text-sm ${hasActiveLink ? '' : 'text-gray-500'}`} />
                              </div>
                            )}
                            <h6 className={`text-xs font-semibold uppercase tracking-wider ${
                              hasActiveLink ? 'text-primary' : 'text-gray-600'
                            }`}>
                              {t(category)}
                            </h6>
                          </div>
                          <span className={hasActiveLink ? 'text-primary' : 'text-gray-400'}>
                            {isGroupCollapsed ? <FaChevronRight className="text-xs" /> : <FaChevronDown className="text-xs" />}
                          </span>
                        </>
                      )}
                    </button>
                  );
                })()}

                {/* Links - with smooth animation */}
                <div
                  className={`transition-all duration-200 ease-in-out ${
                    leftCollapsed
                      ? (hasActiveLink || expandedCollapsedCategory === category ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0')
                      : (isGroupCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100')
                  }`}
                  style={{ overflow: 'hidden' }}
                >
                  {leftCollapsed ? (
                    // Collapsed sidebar: show icons only
                    <ul className="space-y-1 pb-2 flex flex-col items-center">
                      {links.map((link) => {
                        const isExactMatch = location.pathname === link.path;
                        const isParentMatch = link.path !== "/" && link.path !== "/products" &&
                          location.pathname.startsWith(link.path + '/');
                        const isProductsMatch = link.path === "/products" &&
                          (location.pathname === "/" || location.pathname === "/products");
                        const isActive = isExactMatch || isParentMatch || isProductsMatch;

                        return (
                          <li key={link.path}>
                            <Link
                              to={link.to || link.path}
                              className={`flex items-center justify-center p-2 rounded-md transition-colors ${
                                isActive
                                  ? 'bg-primary bg-opacity-10 text-primary'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              title={t(link.text)}
                              onClick={() => {
                                setClickedPath(link.path);
                              }}
                            >
                              <span className="text-lg">{link.icon}</span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    // Expanded sidebar: show full links
                    <ul className="space-y-1 px-2 pb-2">
                      {links.map((link) => {
                        const isExactMatch = location.pathname === link.path;
                        const isParentMatch = !isExactMatch &&
                          link.path !== "/" &&
                          link.path !== "/products" &&
                          location.pathname.startsWith(link.path + '/') &&
                          !links.some(l => l.path !== link.path && location.pathname === l.path);
                        const isProductsMatch = link.path === "/products" &&
                          (location.pathname === "/" || location.pathname === "/products");

                        const isActive = isExactMatch || isParentMatch || isProductsMatch;
                        const isClicked = clickedPath === link.path;
                        const shouldHighlight = isActive || isClicked;

                        const isFav = isFavorite(link.path);
                        return (
                          <li key={link.path} className="group">
                            <div className="flex items-center">
                              <Link
                                to={link.to || link.path}
                                className={`flex-1 flex items-center py-2 px-3 rounded-md transition-colors ${
                                  shouldHighlight
                                    ? 'bg-primary bg-opacity-10 text-primary'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                onClick={(e) => {
                                  setClickedPath(link.path);
                                  if (isMobile) {
                                    toggleLeftSidebar(true);
                                  }
                                }}
                              >
                                <span className="mr-3">{link.icon}</span>
                                <span>{t(link.text)}</span>
                              </Link>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleFavorite(link.path, link.text);
                                }}
                                className={`p-1 rounded transition-all flex-shrink-0 ${
                                  isFav
                                    ? 'text-yellow-400 opacity-100'
                                    : 'text-gray-300 opacity-0 group-hover:opacity-100 hover:text-yellow-400'
                                }`}
                                title={isFav ? t('Remove from favorites') : t('Add to favorites')}
                              >
                                {isFav ? <FaStar className="text-sm" /> : <FiStar className="text-sm" />}
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Banner Section - Below menu */}
        {banners.length > 0 && !leftCollapsed && (
          <div className="px-3 pb-4">
            <BannerSlider
              banners={banners}
              locationSettings={bannerLocationSettings}
              autoPlay={true}
              interval={5000}
              showOnMobile={true}
              isMobile={isMobile}
              className="rounded-lg"
            />
          </div>
        )}

        {/* Resize handle - only on desktop and when not collapsed */}
        {!leftCollapsed && !isMobile && (
          <div
            className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary hover:opacity-50"
            onMouseDown={handleLeftMouseDown}
          />
        )}
      </aside>

      <UserSwitchModal
        show={showUserSwitchModal}
        onClose={() => setShowUserSwitchModal(false)}
      />
    </>
  );
};

export default DashboardSidebar;